import type { ArtifactService } from '../services/artifactService'
import type { WireArtifact } from '../types/articles'
import { publishingApiClient } from './publishingApiClient'

export const httpArtifactService: ArtifactService = {
  async upload(draftId, file, type, contentType, classification, disseminationControls, extraMeta): Promise<WireArtifact> {
    // Step 1: register metadata — JSON only, no file bytes
    const { data: artifact } = await publishingApiClient.put<WireArtifact>(
      `/drafts/${draftId}/artifacts`,
      {
        type,
        filename: file.name,
        contentType,
        classification,
        ...(disseminationControls && disseminationControls.length > 0 ? { disseminationControls } : {}),
        ...extraMeta,
      },
    )

    // Step 2: PUT binary directly to S3 via presigned URL (bypasses API GW)
    // Use fetch (not axios) so no extra headers are sent beyond Content-Type + host.
    // The presigned URL is signed for exactly those two headers; any additional
    // headers (e.g. axios's default Accept) break the signature or trigger a
    // CORS preflight that S3 rejects.
    if (!artifact.presignedUploadUrl) {
      throw new Error('PUT /drafts/{id}/artifacts response missing presignedUploadUrl')
    }
    const s3Response = await fetch(artifact.presignedUploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    })
    if (!s3Response.ok) {
      throw new Error(`S3 upload failed: ${s3Response.status} ${s3Response.statusText}`)
    }

    return artifact
  },

  async delete(draftId, artifactId): Promise<void> {
    await publishingApiClient.delete(`/drafts/${draftId}/artifacts/${artifactId}`)
  },

  resolveUrl(artifactId, artifacts): string {
    return artifacts.find(a => a.id === artifactId)?.presignedUrl ?? ''
  },
}

// ── Alias kept so ServiceProvider import compiles without rename ───────────────
export { httpArtifactService as httpImageService }
